import { DataTypeProvider, GroupingState, IntegratedGrouping, TableColumnWidthInfo } from '@devexpress/dx-react-grid';
import {
	Grid,
	TableColumnReordering,
	TableColumnResizing,
	TableGroupRow,
	TableHeaderRow,
	VirtualTable,
} from '@devexpress/dx-react-grid-bootstrap4';
import { IPredictionGroupingDto } from 'api/api';
import moment from 'moment';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOrder, setSize } from 'state/ducks/prediction-table-settings/operations';
import {
	predictionAmountOfDaysColumns,
	PredictionColumns,
	predictionDateColumns,
	PredictionGroupingDtoKeys,
} from 'state/ducks/prediction-table-settings/types';
import { TableColumnWidthInfoGeneric } from 'state/ducks/table-settings/types';
import { localized, localizedInterpolation } from 'state/i18n';
import { AppState } from 'state/store';
import { DateFormatterModes, getDateToString } from 'view/shared/components/date-to-string';
import ProgressBar from 'view/shared/components/progress-bar/progress-bar';
import { ROW_HEIGHT } from './bi-table';

interface Props {
	data: IPredictionGroupingDto[];
	loading?: boolean;
}

const rootStyle: React.CSSProperties = { height: '100%' };
const Root = (props: Grid.RootProps) => (
	<Grid.Root className="devextreme-react-grid-remove-margin-bottom" {...props} style={rootStyle} />
);

const TableComponent = ({ ...restProps }) => <VirtualTable.Table {...restProps} className="bi-report-table-striped" />;
const HeaderComponent = ({ ...restProps }) => <VirtualTable.TableHead {...restProps} className="table-header" />;
const TableRow = (obj: any) => <VirtualTable.Row {...obj} />;

const getRowId = (row: any) => {
	return row.id ?? `${row.outerCollapsible}_${row.predictionDate}_${row.predictedDaysUntilFull}`;
};

const PredictionTable: FC<Props> = memo(props => {
	const { data, loading } = props;
	const dispatch = useDispatch();
	const columnOrder = useSelector((state: AppState) => state.predictionTableSettingsReducer.columnOrder);
	const columnWidths = useSelector((state: AppState) => state.predictionTableSettingsReducer.columnWidths);
	const columnsAll = useSelector((state: AppState) => state.predictionTableSettingsReducer.columns);

	const rows = useMemo(() => {
		const machineStats = new Map<
			string,
			{
				totalError: number;
				count: number;
				latestEmptiedDate?: Date;
				latestFillLevel?: number;
			}
		>();

		data.forEach(group => {
			const sn = group.serialNumber;
			if (!sn) return;

			const current = machineStats.get(sn) || { totalError: 0, count: 0 };

			if (group.actualEmptiedDate) {
				const groupDate = new Date(group.actualEmptiedDate);
				if (!current.latestEmptiedDate || groupDate > current.latestEmptiedDate) {
					current.latestEmptiedDate = groupDate;
					current.latestFillLevel = group.fillLevelWhenEmptied;
				}
			}

			if (group.predictions) {
				group.predictions.forEach(p => {
					if (group.actualEmptiedDate && group.fillLevelWhenEmptied === 100 && p.predictionDate) {
						const predictedDate = moment(p.predictionDate).add(p.predictedMinutesUntilFull, 'minutes');
						const err = Math.abs(moment(group.actualEmptiedDate).diff(predictedDate, 'days', true));

						current.totalError += err;
						current.count += 1;
					}
				});
			}

			machineStats.set(sn, current);
		});

		return data
			.flatMap(group => {
				const sn = group.serialNumber;
				const stats = sn ? machineStats.get(sn) : undefined;

				const avgErr = stats && stats.count > 0 ? stats.totalError / stats.count : undefined;
				const avgErrString =
					avgErr !== undefined ? `${avgErr.toFixed(2)} ${localized('days')}` : localized('Unknown');

				let latestEmptiedString = '';
				if (stats?.latestEmptiedDate) {
					const datePart = stats.latestEmptiedDate.toLocaleDateString();
					const fillPart = `${stats.latestFillLevel}%`;
					latestEmptiedString = ` | ${localizedInterpolation('LastEmptiedAtIntWithIntFill', {
						date: datePart,
						fill: fillPart,
					})}`;
				}

				const outerGroupStr = sn
					? `${sn} | ${localized('AverageError')}: ${avgErrString}${latestEmptiedString}`
					: localized('Unknown');

				let innerTotalError = 0;
				let innerCount = 0;

				if (group.actualEmptiedDate && group.fillLevelWhenEmptied === 100 && group.predictions) {
					group.predictions.forEach(p => {
						if (p.predictionDate) {
							const predictedDate = moment(p.predictionDate).add(p.predictedMinutesUntilFull, 'minutes');
							const err = Math.abs(moment(group.actualEmptiedDate!).diff(predictedDate, 'days', true));
							innerTotalError += err;
							innerCount += 1;
						}
					});
				}

				const innerAvgErr = innerCount > 0 ? innerTotalError / innerCount : undefined;
				const innerAvgErrString =
					innerAvgErr !== undefined
						? ` | ${localized('Error')}: ${innerAvgErr.toFixed(2)} ${localized('days')}`
						: '';

				const innerGroupStr = group.actualEmptiedDate
					? `${localizedInterpolation('EmptiedAtIntWithIntFill', {
							date: new Date(group.actualEmptiedDate).toLocaleDateString(),
							fill: `${group.fillLevelWhenEmptied}%`,
					  })}${innerAvgErrString}`
					: localized('NotEmptiedYet');

				return group?.predictions?.map(
					(prediction): PredictionColumns => {
						const predictedDate = moment(prediction.predictionDate).add(
							prediction.predictedMinutesUntilFull,
							'minutes'
						);

						return {
							...prediction,
							predictedDaysUntilFull: prediction.predictedMinutesUntilFull / (60 * 24),
							actualEmptiedDate: group.actualEmptiedDate ?? new Date(1970),
							predictedDate: predictedDate.toDate(),

							innerCollapsible: innerGroupStr,
							outerCollapsible: outerGroupStr,

							predictedFillLevel: (() => {
								if (
									prediction.predictedFillLevel !== null &&
									prediction.predictedFillLevel !== undefined
								)
									return prediction.predictedFillLevel;

								if (!predictedDate || !group.previousEmptyingDate || !prediction.predictionDate) {
									return undefined;
								}
								const startDate = moment(group.previousEmptyingDate);
								const endDate = moment(predictedDate);
								const predictionDate = moment(prediction.predictionDate);
								const totalDuration = endDate.diff(startDate, 'minutes');
								const elapsedDuration = predictionDate.diff(startDate, 'minutes');

								if (totalDuration <= 0) {
									return predictionDate.isAfter(endDate) ? 100 : 0;
								}
								const progress = (elapsedDuration / totalDuration) * 100;
								return Math.round(Math.max(0, Math.min(progress, 100)));
							})(),
							predictionError: group.actualEmptiedDate
								? Math.abs(moment(group.actualEmptiedDate).diff(predictedDate, 'days', true))
								: undefined,
						};
					}
				);
			})
			.filter(r => r !== undefined);
	}, [data]);

	const sortedRows = useMemo(() => {
		return [...rows].sort((a, b) => {
			const dateA = new Date(a!.actualEmptiedDate).getTime();
			const dateB = new Date(b!.actualEmptiedDate).getTime();

			if (dateA > dateB) return -1;
			if (dateA < dateB) return 1;

			const predA = new Date(a!.predictionDate).getTime();
			const predB = new Date(b!.predictionDate).getTime();

			if (predA > predB) return -1;
			if (predA < predB) return 1;

			return 0;
		});
	}, [rows]);

	const DateFormatter = useCallback(
		(obj: { value?: Date }) =>
			obj.value ? (
				<span>{getDateToString(obj.value, DateFormatterModes.withMinutes)}</span>
			) : (
				<span>{localized('NoData')}</span>
			),
		[]
	);

	const DaysFormatter = useCallback(
		(obj: { value?: number }) =>
			obj.value !== undefined ? (
				<span>
					{obj.value.toFixed(2)} {localized('days')}
				</span>
			) : (
				<span>{localized('NoData')}</span>
			),
		[]
	);

	const FillLevelFormatter = useCallback(
		(obj: { value?: number }) =>
			obj.value !== undefined ? (
				<ProgressBar value={obj.value} maxValue={100} />
			) : (
				<span>{localized('NoData')}</span>
			),
		[]
	);

	const changeColumnWidths = useCallback(
		(columnWidths: TableColumnWidthInfo[]) =>
			dispatch(setSize(columnWidths as TableColumnWidthInfoGeneric<PredictionGroupingDtoKeys>[])),
		[dispatch]
	);

	const changeColumnOrder = useCallback(
		(order: string[]) => dispatch(setOrder(order as PredictionGroupingDtoKeys[])),
		[dispatch]
	);

	return (
		<div style={{ position: 'relative', height: '100%', minHeight: '200px' }}>
			{loading && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(255, 255, 255, 0.7)',
						zIndex: 999,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<div className="spinner-border text-primary" role="status">
						<span className="sr-only">Loading...</span>
					</div>
				</div>
			)}

			<Grid rows={sortedRows} columns={columnsAll} getRowId={getRowId} rootComponent={Root}>
				<GroupingState
					defaultGrouping={[{ columnName: 'outerCollapsible' }, { columnName: 'innerCollapsible' }]}
					defaultExpandedGroups={[]}
				/>
				<IntegratedGrouping />
				<DataTypeProvider for={predictionDateColumns} formatterComponent={DateFormatter} />
				<DataTypeProvider for={predictionAmountOfDaysColumns} formatterComponent={DaysFormatter} />
				<DataTypeProvider for={['predictedFillLevel']} formatterComponent={FillLevelFormatter} />
				<VirtualTable
					tableComponent={TableComponent}
					headComponent={HeaderComponent}
					columnExtensions={columnWidths}
					rowComponent={TableRow}
					estimatedRowHeight={ROW_HEIGHT}
				/>
				<TableColumnReordering onOrderChange={changeColumnOrder} order={columnOrder} />
				<TableColumnResizing onColumnWidthsChange={changeColumnWidths} columnWidths={columnWidths} />
				<TableGroupRow />
				<TableHeaderRow />
			</Grid>
		</div>
	);
});

export default PredictionTable;
